import { AbstractQueue } from './queue.factory';
import { Cesium } from '../../../../cesium';
import { JulianDate } from 'cesium';

/**
 * @property {number} minSegmentSize The minimum interval size return through nextItem()
 * @property {number} maxSegmentSize The maximum interval size return through nextItem()
 * @property {number} padding The number of seconds that will be added on to both sides of any interval added to this queue (through addInterval())
 */
export interface TimeIntervalChunkerConfig {
    minSegmentSize: number;
    maxSegmentSize: number;
    padding: number;
}

const defaultConfig: TimeIntervalChunkerConfig = {
    minSegmentSize: 10,
    maxSegmentSize: 300,
    padding: 300,
};
/**
 * Takes a Cesium.TimeInterval object and handles chunking(segmenting) it to a certain min/max segment size,
 * as well as storing the fetch state of each chunk. It also allows retrieving the chunk closest to a particular time.
 *
 * Usage example: storing the state of ImsTimeCollection data loading from the API.
 */
export class TimeIntervalChunker extends AbstractQueue {
    private config: TimeIntervalChunkerConfig;
    private queue: Cesium.TimeIntervalCollection;

    static get QUEUED(): null {
        return null;
    }

    static get PENDING(): number {
        return 0;
    }

    static get FETCHED(): number {
        return 1;
    }

    static get ERROR(): number {
        return 2;
    }

    /**
     * * TimeIntervalChunker is an object that handles chunking a larger time interval.
     * It is useful for providing an interval and retrieving it in chunks of a specified "segment" size.
     * Intervals can be added to it with addInterval(), or requested from it with nextItem()
     * When an interval is requested from the queue, through nextItem(), a time interval is returned that is:
     * between minSegmentSize and maxSegmentSize seconds in length,
     * part of the main interval,
     * and automatically picked closest to the cursor time;
     * The TimeIntervalChunker also stores the load/fetch state of the interval's chunks.
     * @param config {TimeIntervalChunkerConfig}
     */

    constructor(config: TimeIntervalChunkerConfig = defaultConfig) {
        super();
        this.config = {
            ...defaultConfig,
            ...config,
        };
        this.queue = new Cesium.TimeIntervalCollection();
    }

    /**
     * @param interval The Cesium Time Interval to add
     * @param status The status to set the interval (overrides data if provided)
     * @param clear Whether this item should clear the rest of the queue
     * @param pad Whether to pad the interval
     * @returns {Cesium.TimeInterval} The interval, padded if required
     */
    addInterval(interval, status = null, clear = false, pad = false): Cesium.TimeInterval {
        if (interval instanceof Cesium.TimeInterval === false) {
            throw 'Can only add Cesium Time Intervals';
        }
        const { padding } = this.config;
        if (pad) {
            interval.start = TimeIntervalChunker.padJulian(interval.start, -padding);
            interval.stop = TimeIntervalChunker.padJulian(interval.stop, padding);
        }

        // Work out why this is here
        if (status !== null) {
            interval.data = status;
        }

        this.queue = TimeIntervalChunker.merge(this.queue, interval, clear);

        return interval;
    }

    /**
     * Get the start time of the queue
     */
    getStartTime(): JulianDate {
        return this.queue.start;
    }

    /**
     * Get the stop time of the queue
     */
    getStopTime(): JulianDate {
        return this.queue.stop;
    }

    /**
     * Returns false if there's any intervals in the queue that still need to be fetched
     * @returns {boolean}
     */
    isEmpty(): boolean {
        // Anything other than 0 will return false
        const { minSegmentSize } = this.config;
        return !this.getIntervals(null, minSegmentSize).length;
    }

    /**
     * Clears the queue
     */
    clear(): void {
        this.queue.removeAll();
    }

    /**
     * Sets all items in the current queue to the un-fetched state
     */
    markAllAsUnfetched(): void {
        TimeIntervalChunker.reset(
            this.queue,
            this.queue.start,
            this.queue.stop,
            TimeIntervalChunker.QUEUED,
        );
    }

    /**
     * Gets the next item from the queue and marks it as pending
     * @param perfectTime The time to grab as close as possible to
     * @param imperfectTime The time to never grab after
     * @returns {*} Either an interval or null if no valid interval was found
     */
    nextItem(perfectTime: JulianDate, imperfectTime: JulianDate): Cesium.TimeInterval {
        const { minSegmentSize, maxSegmentSize } = this.config;
        const interval = TimeIntervalChunker.getInterval(
            this.queue,
            TimeIntervalChunker.QUEUED,
            perfectTime,
            imperfectTime,
            minSegmentSize,
            maxSegmentSize,
        );

        if (!interval) {
            return null;
        }

        // Add it back to the queue with the new status (update the queue) and return it
        return this.addInterval(interval, TimeIntervalChunker.PENDING, false, false);
    }

    getIntervals(
        data: any,
        minSize: number | undefined = undefined,
        maxSize: number | undefined = undefined,
    ): Cesium.TimeIntervalCollection {
        // TODO Tests and perf
        const collection = new Cesium.TimeIntervalCollection();
        for (let i = 0; i !== this.queue.length; ++i) {
            const interval = this.queue.get(i);
            const distance = Cesium.JulianDate.secondsDifference(interval.stop, interval.start);
            if (
                interval.data == data &&
                (!maxSize || distance < maxSize) &&
                (!minSize || distance > minSize)
            ) {
                collection.addInterval(interval);
            }
        }
        return collection;
    }

    printLogs() {
        let pending = 0;
        let fetched = 0;
        let waiting = 0;
        for (let i = 0; i !== this.queue.length; ++i) {
            const interval = this.queue.get(i);
            switch (interval.data) {
                case TimeIntervalChunker.QUEUED: {
                    waiting += Cesium.JulianDate.secondsDifference(interval.stop, interval.start);
                    break;
                }
                case TimeIntervalChunker.PENDING: {
                    pending += Cesium.JulianDate.secondsDifference(interval.stop, interval.start);
                    break;
                }
                case TimeIntervalChunker.FETCHED: {
                    fetched += Cesium.JulianDate.secondsDifference(interval.stop, interval.start);
                    break;
                }
            }
        }
        // unused?
        // log('waiting ', waiting, 'pending', pending, 'fetched', fetched);
    }

    static padJulian(startJulian: JulianDate, padding: number): JulianDate {
        return Cesium.JulianDate.addSeconds(startJulian, padding, new Cesium.JulianDate());
    }

    /**
     * Resets all intervals in a certain range (thereby merging them together too)
     * @param collection The collection to reset
     * @param start The starting point to reset
     * @param stop The end point to reset
     * @param data The data to 'reset' to
     */
    static reset(
        collection: Cesium.TimeIntervalCollection,
        start: JulianDate,
        stop: JulianDate,
        data,
    ): void {
        // TODO Tests
        // Intervals that have the same data (reference equality) are merged together
        // Intervals that overlap are overwritten.
        collection.addInterval(
            new Cesium.TimeInterval({
                start: start,
                stop: stop,
                isStartIncluded: true,
                isStopIncluded: true,
                data: data,
            }),
        );
    }

    /**
     * @param collection The Cesium Time Interval Collection to merge with
     * @param interval The Cesium Time Interval to merge in
     * @param clear Whether the returned collection should be limited to the size of the interval merged, and also underwrite rather than overwrite interesting intervals
     * @returns {*} The new merged collection
     * @private
     */
    static merge(
        collection: Cesium.TimeIntervalCollection,
        interval: Cesium.TimeInterval,
        clear: boolean,
    ): Cesium.TimeIntervalCollection {
        if (!clear) {
            // TODO find a better definition, (it really means underwrite + shift)
            // Intervals that have the same data (reference equality) are merged together
            // Intervals that overlap are overwritten.
            collection.addInterval(interval);
            return collection;
        }

        // Create a collection out of the interval to merge (so we do an intersection)
        const collection2 = new Cesium.TimeIntervalCollection([interval]);
        // This intersection function checks equality of data by reference (arg 2),
        // and merges equal data by returning the 1st piece (from the original collection) (arg 3)
        // TODO, this works as needed, but looking at Cesium's source I don't think the dataComparer (arg 2) is used?
        const intersection = collection.intersect(
            collection2,
            (a, b) => a === b,
            (a, b) => a,
        );

        if (!intersection.length) {
            // There wasn't any intersection, it's an entirely new collection
            return collection2;
        }

        if (Cesium.JulianDate.greaterThan(interval.stop, collection.stop)) {
            // Our new interval is after our collection
            // This means need to add a chunk of the new interval onto the end of the intersection
            intersection.addInterval(
                new Cesium.TimeInterval({
                    start: collection.stop,
                    stop: interval.stop,
                    isStartIncluded: true,
                    isStopIncluded: true,
                    data: interval.data,
                }),
            );
        }

        if (Cesium.JulianDate.lessThan(interval.start, collection.start)) {
            // Our new interval starts before our collection
            // This means need to add a chunk of the new interval onto the start of the intersection
            intersection.addInterval(
                new Cesium.TimeInterval({
                    start: interval.start,
                    stop: collection.start,
                    isStartIncluded: true,
                    isStopIncluded: true,
                    data: interval.data,
                }),
            );
        }
        // Because we used an intersection, we don't have to worry about deleting parts of the collection that don't overlap with the new interval.
        // But it means you have to grab the returned result.
        return intersection;
    }

    /**
     * Grabs an interval from the collection of x seconds with x data, will try to grab the interval from the perfect time forward without touching the imperfect time.
     * @param collection The Cesium Time Interval Collection to search
     * @param data A item that matches reference equality to find the right interval
     * @param perfectTime A time to aim to grab the closest interval to.
     * @param imperfectTime If provided will only grab chunks before this time
     * @param minSeconds The minimum number of seconds that can be fetched at once
     * @param maxSeconds The maximum number of seconds that can be fetched at once
     * @returns {*} A Cesium Time Interval picked from the collection, or null if any constraints couldn't be met
     * @private
     */
    static getInterval(
        collection: Cesium.TimeIntervalCollection,
        data,
        perfectTime: JulianDate,
        imperfectTime: JulianDate,
        minSeconds: number,
        maxSeconds: number,
    ): Cesium.TimeInterval {
        if (collection.isEmpty) {
            return null;
        }

        // Covert interval collection into an array
        const intervals = [];
        for (let i = 0; i !== collection.length; ++i) {
            const interval = collection.get(i);
            if (interval.data == data) {
                intervals.push(interval);
            }
        }

        return TimeIntervalChunker.selectInterval(
            intervals,
            perfectTime,
            imperfectTime,
            minSeconds,
            maxSeconds,
        );
    }

    /**
     * Selects a portion of one of the intervals given which is as close to the perfect time as possible and never includes of is after the imperfect time
     * @param intervals
     * @param perfectTime
     * @param imperfectTime
     * @param minSeconds
     * @param maxSeconds
     */
    static selectInterval(
        intervals: Cesium.TimeInterval[],
        perfectTime: JulianDate,
        imperfectTime: JulianDate,
        minSeconds: number,
        maxSeconds: number,
    ): Cesium.TimeInterval {
        return intervals.reduce((previous, interval) => {
            const currentValidSeconds =
                Cesium.JulianDate.secondsDifference(imperfectTime, interval.start) >= minSeconds;
            const currentContainsPerfect = Cesium.TimeInterval.contains(interval, perfectTime);
            const previousContainsPerfect = previous
                ? Cesium.TimeInterval.contains(previous, perfectTime)
                : false;

            const currentStartDistanceFromPerfect = Math.abs(
                Cesium.JulianDate.secondsDifference(interval.start, perfectTime),
            );
            const currentStopDistanceFromPerfect = Math.abs(
                Cesium.JulianDate.secondsDifference(interval.stop, perfectTime),
            );
            const previousStartDistanceFromPerfect = previous
                ? Math.abs(Cesium.JulianDate.secondsDifference(previous.start, perfectTime))
                : null;
            const previousStopDistanceFromPerfect = previous
                ? Math.abs(Cesium.JulianDate.secondsDifference(previous.stop, perfectTime))
                : null;

            const currentDisplacementFromPerfect = Math.min(
                currentStartDistanceFromPerfect,
                currentStopDistanceFromPerfect,
            );
            const previousDisplacementFromPerfect = previous
                ? Math.min(previousStartDistanceFromPerfect, previousStopDistanceFromPerfect)
                : Infinity;

            // If the current interval contains the perfect or it's is closer, and our previous didn't contain the perfect
            // and our current interval has enough valid seconds
            if (
                (currentContainsPerfect ||
                    (currentDisplacementFromPerfect < previousDisplacementFromPerfect &&
                        !previousContainsPerfect)) &&
                currentValidSeconds
            ) {
                // Go from the perfect time forward (or the start of the interval)
                const initialStart = Cesium.JulianDate.greaterThan(interval.start, perfectTime)
                    ? interval.start.clone()
                    : perfectTime;
                const initialStop = Cesium.JulianDate.addSeconds(
                    initialStart,
                    maxSeconds,
                    new Cesium.JulianDate(),
                );
                // How much this stop time is over the end of the interval or the imperfect time
                const distanceOverLimits = Math.max(
                    0,
                    Cesium.JulianDate.secondsDifference(initialStop, interval.stop),
                    Cesium.JulianDate.secondsDifference(initialStop, imperfectTime),
                );
                // Move the interval back that much
                const prospectiveStart = Cesium.JulianDate.addSeconds(
                    initialStart,
                    -distanceOverLimits,
                    new Cesium.JulianDate(),
                );
                const prospectiveStop = Cesium.JulianDate.addSeconds(
                    initialStop,
                    -distanceOverLimits,
                    new Cesium.JulianDate(),
                );

                // If we pushed out start back too far, move it back to the interval start
                const fittedStart = Cesium.JulianDate.greaterThan(interval.start, prospectiveStart)
                    ? interval.start.clone()
                    : prospectiveStart;

                // If our interval is going to leave behind another interval smaller than the minimum size, consume that bit too
                const finalStart =
                    Cesium.JulianDate.secondsDifference(fittedStart, interval.start) <= minSeconds
                        ? interval.start.clone()
                        : fittedStart;
                const fittedStop =
                    Cesium.JulianDate.secondsDifference(interval.stop, prospectiveStop) <=
                        minSeconds &&
                    Cesium.JulianDate.lessThanOrEquals(interval.stop, imperfectTime)
                        ? interval.stop.clone()
                        : prospectiveStop;

                // // If our interval is now shorter than the minimum seconds, our stop is null
                const finalStop =
                    Cesium.JulianDate.secondsDifference(fittedStop, finalStart) >= minSeconds
                        ? fittedStop
                        : null;

                // If there was no stop to this interval, then it's return the previous interval
                if (finalStop) {
                    return new Cesium.TimeInterval({
                        start: finalStart,
                        stop: finalStop,
                        isStartIncluded: true,
                        isStopIncluded: true,
                        data: interval.data,
                    });
                }
            }
            // This will continually remain null unless all the above criteria matches an interval
            return previous;
        }, null);
    }
}
